/**
 * Get the base URL for the application
 * Uses NEXT_PUBLIC_APP_DOMAIN first, falls back to VERCEL_URL, then localhost
 */
export const getBaseUrl = (): string => {
  // Use custom domain if configured
  if (process.env.NEXT_PUBLIC_APP_DOMAIN) {
    return `${process.env.NEXT_PUBLIC_APP_DOMAIN}`;
  }

  // Fall back to Vercel URL in production (automatically provided by Vercel)
  if (process.env.VERCEL_URL) {
    const protocol =
      process.env.VERCEL_ENV === "development" ? "http" : "https";
    return `${protocol}://${process.env.VERCEL_URL}`;
  }

  // Final fallback for local development
  return "http://localhost:3000";
};

/**
 * Build an org-prefixed URL
 * @param orgSlug - The organization slug
 * @param path - The path within the org (should start with /)
 * @returns The full org-prefixed path (e.g., /acme-corp/projects)
 */
export const buildOrgUrl = (orgSlug: string, path: string): string => {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `/${orgSlug}${cleanPath}`;
};

/**
 * Build a project URL within an org
 * @param orgSlug - The organization slug
 * @param projectId - The project ID
 * @param subPath - Optional sub-path within the project (e.g., /estimator)
 * @returns The full project path (e.g., /acme-corp/projects/abc123/estimator)
 */
export const buildProjectUrl = (
  orgSlug: string,
  projectId: string,
  subPath = ""
): string => {
  const cleanSubPath =
    subPath && !subPath.startsWith("/") ? `/${subPath}` : subPath;
  return `/${orgSlug}/projects/${projectId}${cleanSubPath}`;
};

/**
 * Build an admin URL within an org
 * @param orgSlug - The organization slug
 * @param subPath - Optional sub-path within admin (e.g., /rules-builder)
 * @returns The full admin path (e.g., /acme-corp/admin/rules-builder)
 */
export const buildAdminUrl = (orgSlug: string, subPath = ""): string => {
  const cleanSubPath =
    subPath && !subPath.startsWith("/") ? `/${subPath}` : subPath;
  return `/${orgSlug}/admin${cleanSubPath}`;
};

/**
 * Check if a pathname is within a base path (exact match or nested sub-route)
 * Segment-aware: "/a/vendors-map" does NOT match base "/a/vendors"
 * @param pathname - The current URL pathname
 * @param basePath - The base path to check against
 * @returns True if pathname equals basePath or is nested under it
 */
export const isPathActive = (pathname: string, basePath: string): boolean =>
  pathname === basePath || pathname.startsWith(`${basePath}/`);

/**
 * Extract the organization slug from a URL path
 * Assumes URL structure: /[orgSlug]/...
 * @param pathname - The URL pathname
 * @returns The org slug or null if not found
 */
export const extractOrgSlugFromPath = (pathname: string): string | null => {
  const pathParts = pathname.split("/").filter(Boolean);
  if (pathParts.length === 0) return null;

  const potentialSlug = pathParts[0];

  // Exclude known public/special routes that aren't org slugs
  const nonOrgPrefixes = [
    "sign-in",
    "forgot-password",
    "reset-password",
    "invite",
    "privacy-policy",
    "no-project-access",
    "api",
    "_next",
  ];

  if (nonOrgPrefixes.includes(potentialSlug)) {
    return null;
  }

  return potentialSlug;
};

/**
 * Check if a path is an org-prefixed route
 * @param pathname - The URL pathname
 * @returns True if the path starts with an org slug
 */
export const isOrgPrefixedRoute = (pathname: string): boolean => {
  return extractOrgSlugFromPath(pathname) !== null;
};

/**
 * Check if a path is a legacy route (old URL structure without org slug)
 * @param pathname - The URL pathname
 * @returns True if this is a legacy route that needs redirection
 */
export const isLegacyRoute = (pathname: string): boolean => {
  return (
    pathname.startsWith("/projects") ||
    pathname.startsWith("/admin") ||
    pathname === "/"
  );
};
